import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { ListWorkflowTemplatesResponseJSON } from "@performyard/service-contracts";

import {
	GetWorkflowTemplateResponseJSON,
	ListWorkflowTemplatesParams,
	SaveWorkflowTemplatePayload,
	createWorkflowTemplate,
	deleteWorkflowTemplate,
	getWorkflowTemplate,
	getWorkflowTemplates,
	updateWorkflowTemplate,
} from "../../services/defs/workflow-templates";
import type { PYUseMutationOptions, PYUseQueryOptions } from "../StandardQueryArguments";

export const workflowTemplatesKeys = {
	all: ["WorkflowTemplates"] as const,
	list: (params?: ListWorkflowTemplatesParams) => [...workflowTemplatesKeys.all, "list", params] as const,
	detail: (workflowTemplateId: string) => [...workflowTemplatesKeys.all, "detail", workflowTemplateId] as const,
};

export const useWorkflowTemplates = (
	params: ListWorkflowTemplatesParams = {},
	options?: PYUseQueryOptions<ListWorkflowTemplatesResponseJSON>
) => {
	return useQuery({
		queryKey: workflowTemplatesKeys.list(params),
		queryFn: () => getWorkflowTemplates(params),
		meta: {
			errorMessage: "There was an error getting the workflow templates.",
		},
		...options,
	});
};

export const useWorkflowTemplate = (
	workflowTemplateId: string,
	options?: PYUseQueryOptions<GetWorkflowTemplateResponseJSON>
) => {
	return useQuery({
		queryKey: workflowTemplatesKeys.detail(workflowTemplateId),
		queryFn: () => getWorkflowTemplate(workflowTemplateId),
		enabled: !!workflowTemplateId,
		meta: {
			errorMessage: "There was an error getting the workflow template.",
		},
		...options,
	});
};

/**
 * Save/delete errors are surfaced by the editor itself (the legacy 422 payload carries
 * field-specific messages worth showing), so the default error toast is disabled and
 * `options` spread before `onSuccess` so callers can't clobber the invalidation.
 */
export const useCreateWorkflowTemplate = (options?: PYUseMutationOptions) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (payload: SaveWorkflowTemplatePayload) => createWorkflowTemplate(payload),
		meta: { disableDefaultErrorHandling: true },
		...options,
		onSuccess: (...args) => {
			queryClient.invalidateQueries({ queryKey: workflowTemplatesKeys.all });
			options?.onSuccess?.(...args);
		},
	});
};

export const useUpdateWorkflowTemplate = (options?: PYUseMutationOptions) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			workflowTemplateId,
			payload,
		}: {
			workflowTemplateId: string;
			payload: SaveWorkflowTemplatePayload;
		}) => updateWorkflowTemplate(workflowTemplateId, payload),
		meta: { disableDefaultErrorHandling: true },
		...options,
		onSuccess: (...args) => {
			queryClient.invalidateQueries({ queryKey: workflowTemplatesKeys.all });
			options?.onSuccess?.(...args);
		},
	});
};

export const useDeleteWorkflowTemplate = (options?: PYUseMutationOptions) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (workflowTemplateId: string) => deleteWorkflowTemplate(workflowTemplateId),
		meta: { disableDefaultErrorHandling: true },
		...options,
		onSuccess: (data, workflowTemplateId, ...rest) => {
			queryClient.removeQueries({ queryKey: workflowTemplatesKeys.detail(workflowTemplateId) });
			queryClient.invalidateQueries({ queryKey: workflowTemplatesKeys.all });
			options?.onSuccess?.(data, workflowTemplateId, ...rest);
		},
	});
};
